import { Interpreter, interpret, ServiceConfig, ActionFunctionMap, createMachine } from 'xstate';
import { BUFFERING_STATES, SEEKING_STATES, PLAYBACK_STATES } from '@quickplay/player-interface';

export interface PlayerContext {
  duration: number;
  shouldRetryOnErrors: boolean;
  maxRetries: number;
  retiresAttempted: number;
  autoPlayOnLoad: boolean;
  repeatWhenFinished: boolean;
  initialPlayback: boolean;
}
export enum PLAYER_STATES {
  SEEKING = 'seeking',
  BUFFERING = 'buffering',
  PLAYER = 'player',
}

export type PlaybackEvent =
  | { type: 'LOAD_EVENT' }
  | { type: 'START_EVENT' }
  | { type: 'PLAY_EVENT' }
  | { type: 'PAUSE_EVENT' }
  | { type: 'SEEKING_START_EVENT'; time: number }
  | { type: 'STOP_EVENT' }
  | { type: 'SEEKING_END_EVENT' }
  | { type: 'BUFFERING_END_EVENT' }
  | { type: 'BUFFERING_START_EVENT' }
  | { type: 'PLAYER_ERROR_EVENT' }
  | { type: 'PLAYER_STOPPED_EVENT' }
  | { type: 'ABORT_EVENT' }
  | { type: 'PLAYER_PLAYED_EVENT' }
  | { type: 'PLAYER_PAUSED_EVENT' }
  | { type: 'PLAYER_ENDED_EVENT' }
  | { type: 'PLAYER_IDLE_EVENT' }
  | { type: 'PLAYER_OPTIONS_CHANGED_EVENT'; context: PlayerContext };

const seeking = {
  id: PLAYER_STATES.SEEKING,
  initial: SEEKING_STATES.INACTIVE,
  states: {
    [SEEKING_STATES.ACTIVE]: {
      entry: ['seekVideo'],
      on: {
        SEEKING_END_EVENT: SEEKING_STATES.INACTIVE,
      },
    },
    [SEEKING_STATES.INACTIVE]: {
      on: {
        SEEKING_START_EVENT: SEEKING_STATES.ACTIVE,
      },
    },
  },
};

const buffering = {
  id: PLAYER_STATES.BUFFERING,
  initial: BUFFERING_STATES.INACTIVE,
  states: {
    [BUFFERING_STATES.ACTIVE]: {
      on: {
        BUFFERING_END_EVENT: BUFFERING_STATES.INACTIVE,
      },
    },
    [BUFFERING_STATES.INACTIVE]: {
      on: {
        BUFFERING_START_EVENT: BUFFERING_STATES.ACTIVE,
      },
    },
  },
};

const player = {
  id: PLAYER_STATES.PLAYER,
  initial: PLAYBACK_STATES.IDLE,
  states: {
    [PLAYBACK_STATES.IDLE]: {
      on: {
        LOAD_EVENT: {
          target: PLAYBACK_STATES.LOADING,
        },
        PLAY_EVENT: {
          actions: 'enableAutoPlay',
          target: PLAYBACK_STATES.LOADING,
        },
        PLAYER_OPTIONS_CHANGED_EVENT: {
          actions: 'playerOptionsChanged',
        },
      },
    },
    [PLAYBACK_STATES.LOADING]: {
      invoke: {
        id: 'loadPlayer',
        src: 'loadPlayer',
        onDone: {
          target: PLAYBACK_STATES.LOADED,
        },
        onError: {
          target: PLAYBACK_STATES.STOPPED,
        },
      },
      on: {
        ABORT_EVENT: {
          target: PLAYBACK_STATES.IDLE,
          actions: 'abortPlayer',
        },
        STOP_EVENT: {
          target: PLAYBACK_STATES.IDLE,
          actions: 'stopVideo',
        },
        PLAYER_ERROR_EVENT: PLAYBACK_STATES.STOPPED,
        PLAYER_OPTIONS_CHANGED_EVENT: {
          actions: 'playerOptionsChanged',
        },
      },
    },
    [PLAYBACK_STATES.LOADED]: {
      after: {
        '5': {
          target: PLAYBACK_STATES.STARTED,
          cond: 'isAutoplayEnabled',
          actions: ['playVideo'],
        },
      },
      on: {
        PLAY_EVENT: {
          target: PLAYBACK_STATES.STARTED,
          actions: ['playVideo'],
        },
        ABORT_EVENT: {
          target: PLAYBACK_STATES.IDLE,
          actions: 'abortPlayer',
        },
        STOP_EVENT: {
          target: PLAYBACK_STATES.IDLE,
          actions: 'stopVideo',
        },
        PLAYER_ERROR_EVENT: PLAYBACK_STATES.STOPPED,
        PLAYER_PLAYED_EVENT: PLAYBACK_STATES.STARTED,
      },
    },
    [PLAYBACK_STATES.STARTED]: {
      on: {
        PAUSE_EVENT: {
          target: PLAYBACK_STATES.PAUSED,
          actions: 'pauseVideo',
        },
        STOP_EVENT: {
          target: PLAYBACK_STATES.IDLE,
          actions: 'stopVideo',
        },
        ABORT_EVENT: {
          target: PLAYBACK_STATES.IDLE,
          actions: 'abortPlayer',
        },
        PLAYER_PAUSED_EVENT: PLAYBACK_STATES.PAUSED,
        PLAYER_ERROR_EVENT: PLAYBACK_STATES.STOPPED,
        PLAYER_STOPPED_EVENT: PLAYBACK_STATES.STOPPED,
        PLAYER_ENDED_EVENT: PLAYBACK_STATES.LOADED,
        PLAYER_IDLE_EVENT: PLAYBACK_STATES.STOPPED,
      },
    },
    [PLAYBACK_STATES.PAUSED]: {
      on: {
        PLAY_EVENT: {
          target: PLAYBACK_STATES.STARTED,
          actions: 'playVideo',
        },
        ABORT_EVENT: {
          target: PLAYBACK_STATES.IDLE,
          actions: 'abortPlayer',
        },
        STOP_EVENT: {
          target: PLAYBACK_STATES.IDLE,
          actions: 'stopVideo',
        },
        PLAYER_PLAYED_EVENT: PLAYBACK_STATES.STARTED,
        PLAYER_ENDED_EVENT: PLAYBACK_STATES.LOADED,
        PLAYER_IDLE_EVENT: PLAYBACK_STATES.STOPPED,
      },
    },
    [PLAYBACK_STATES.STOPPED]: {
      on: {
        '': [
          {
            target: PLAYBACK_STATES.STARTED,
            cond: 'repeatWhenFinished',
          },
          {
            target: PLAYBACK_STATES.IDLE,
            actions: 'abortPlayer',
          },
        ],
      },
    },
  },
};

function isValidRetry(context: PlayerContext): boolean {
  return context.shouldRetryOnErrors && context.maxRetries > context.retiresAttempted;
}

function repeatWhenFinished(context: PlayerContext): boolean {
  return context.repeatWhenFinished;
}

function isAutoplayEnabled(context: PlayerContext): boolean {
  return context && context.autoPlayOnLoad === true && context.initialPlayback === true;
}

export function createAndInterpretPlayerStateMachine(
  playerContext: PlayerContext,
  actions: ActionFunctionMap<PlayerContext, PlaybackEvent>,
  services: Record<string, ServiceConfig<PlayerContext, PlaybackEvent>>,
): Interpreter<PlayerContext, any, PlaybackEvent> {
  const stateMachine = createMachine<PlayerContext, PlaybackEvent, any>(
    {
      id: 'corePlayer',
      initial: PLAYER_STATES.PLAYER,
      type: 'parallel',
      context: playerContext,
      states: {
        seeking: { ...seeking },
        buffering: { ...buffering },
        player: { ...player },
      },
    },
    {
      guards: {
        isValidRetry: isValidRetry,
        repeatWhenFinished: repeatWhenFinished,
        isAutoplayEnabled: isAutoplayEnabled,
      },
      actions: actions,
      services: services,
    },
  );
  return interpret(stateMachine);
}
